/** @jsxImportSource @emotion/react */
import { AxiosError } from "axios";
import {
  Button,
  Container,
  DateInput,
  Form,
  getUniqueId,
  Icon,
  Input,
  Link,
  Message,
  RadioGroup,
  Stack,
  Text,
  VisuallyHidden,
} from "basis";
import { Separator } from "components/Separator/Separator";
import { getIsProduction } from "library/browser";
import {
  validateAuMobileNumber,
  validateCardNumber,
  validateDateAfterOtherDate,
  validateDateTodayOrGreater,
  validateName,
  validateNzMobileNumber,
  validateOver18YearsOld,
} from "library/formValidation";
import { yesNoOptions } from "library/sharedOptions";
import { createOverseasTravelTicket } from "library/zendeskAPI/overseasTravel";
import { useEventTracking, usePageTracking } from "react-event-tracker";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import {
  ActionType,
  FormValues,
  GetOverseasTravelParams,
  ScreenProps,
} from "./types";

const initialValues: FormValues = {
  firstName: "",
  lastName: "",
  dateOfBirth: { day: "", month: "", year: "" },
  mobileNumber: "",
  countryOfDestination: "",
  additionalCardHolders: "",
  travelStartDate: { day: "", month: "", year: "" },
  travelEndDate: { day: "", month: "", year: "" },
  additionalCards: {
    [getUniqueId()]: { cardNumber: "" },
  },
};

function addNewCardHolderUpdateFn(values) {
  const newAdditionalCards = {
    ...values.additionalCards,
    [getUniqueId()]: {
      cardNumber: "",
    },
  };

  return {
    ...values,
    additionalCards: newAdditionalCards,
  };
}

function removeCardHolderUpdateFn(id: string) {
  return (values) => {
    const newAdditionalCards = {
      ...values.additionalCards,
    };

    delete newAdditionalCards[id];

    return {
      ...values,
      additionalCards: newAdditionalCards,
    };
  };
}

const MAX_ADDITIONAL_CARDS = 5;

export const OverseasTravelFrom = ({ state, dispatch }: ScreenProps) => {
  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "overseas-travel",
      siteSubSection: "",
      pageTitle: "Notify us of when you're travelling overseas",
    },
    user: {
      authenticationStatus: "logged in",
    },
  });
  const { trackEvent } = useEventTracking();

  const { accountInfo } = state;
  const token = accountInfo.cardToken;

  const overseasTravelMutation = useMutation<
    number,
    AxiosError,
    GetOverseasTravelParams
  >(createOverseasTravelTicket, {
    onSuccess(_ticketId) {
      trackEvent({
        event: {
          category: "application",
          action: "application-submitted",
          label: "Submit",
          location: "Overseas Travel",
        },
      });
      dispatch({
        type: ActionType.FORM_SUBMITTED,
      });
    },
  });

  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length === 0) {
      overseasTravelMutation.mutate({
        isProduction: getIsProduction(),
        userName: accountInfo.userName,
        token,
        email: accountInfo.userEmail,
        ...values,
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: { values }, setValues }) => {
        const additionalCardIds = Object.keys(values.additionalCards);
        return (
          <Stack gap="8">
            <Container bg="grey.t05" padding="4">
              <CreditCard
                cardName={accountInfo.cardName}
                cardEndsWith={accountInfo.cardEndsWith}
              />
            </Container>
            <Stack gap="8" width-sm="50%">
              <Stack gap="3">
                <Text>
                  <strong>Email address</strong>
                </Text>
                <Text>{accountInfo.userEmail}</Text>
              </Stack>
              <Input
                name="firstName"
                label="First name"
                validate={validateName}
              />
              <Input
                name="lastName"
                label="Last name"
                validate={validateName}
              />
              <DateInput
                name="dateOfBirth"
                label="Date of birth"
                validate={validateOver18YearsOld}
              />
              <Input
                type="tel"
                name="mobileNumber"
                label="Mobile number"
                validate={
                  accountInfo.isNZ
                    ? validateNzMobileNumber
                    : validateAuMobileNumber
                }
              />
              <DateInput
                name="travelStartDate"
                label="Departure date"
                validateData={{
                  validationErrorMessage: "Departure date can't be in the past",
                }}
                validate={validateDateTodayOrGreater}
              />
              <DateInput
                name="travelEndDate"
                label="Return date"
                validateData={{
                  travelStartDate: values.travelStartDate,
                  validationErrorMessage:
                    "Return date can't be before departure date",
                }}
                validate={validateDateAfterOtherDate}
              />
              <Input
                name="countryOfDestination"
                label="Where are you travelling to?"
              />
              <RadioGroup
                name="additionalCardHolders"
                label="Do you have additional card holders travelling with you?"
                options={yesNoOptions}
              />
              {values.additionalCardHolders === "yes" && (
                <Stack gap="8">
                  {additionalCardIds.map((id, index) => (
                    <Container padding="5" hasBorder key={id}>
                      <Stack gap="4">
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text textStyle="subtitle2">
                            <strong>{`Card ${index + 1}`}</strong>
                          </Text>
                          {additionalCardIds.length > 1 && (
                            <Button
                              variant="icon"
                              onClick={() => {
                                setValues(removeCardHolderUpdateFn(id));
                              }}
                            >
                              <Icon name="trash" />
                              <VisuallyHidden>Remove item</VisuallyHidden>
                            </Button>
                          )}
                        </div>
                        <Separator />
                        <Input
                          name={`additionalCards.${id}.cardNumber`}
                          label="Card number"
                          validate={validateCardNumber}
                        />
                      </Stack>
                    </Container>
                  ))}
                  {additionalCardIds.length < MAX_ADDITIONAL_CARDS && (
                    <Link
                      href="#"
                      newTab={false}
                      onClick={(e) => {
                        e.preventDefault();
                        setValues(addNewCardHolderUpdateFn);
                      }}
                    >
                      Add another card
                    </Link>
                  )}
                </Stack>
              )}
              {overseasTravelMutation.error && (
                <Message severity="blocking" bg="secondary.pink.t30">
                  ERROR MESSAGE
                </Message>
              )}
              <Button
                type="submit"
                width="160"
                loading={overseasTravelMutation.isLoading}
              >
                Submit
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
};
